import { Box, CircularProgress, IconButton, Stack, Tooltip } from "@mui/joy";
import { isLoggedIn, logoutCoach } from "api/coachAuth";
import { clearAuthHeaders } from "api";
import Sheet from "@mui/joy/Sheet";
import PatientList from "components/patient-list/PatientList";
import useIsInappWebview from "hooks/useIsInappWebview";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import Typography from "@mui/joy/Typography";
import React, { useState } from "react";
import CoachFilter from "../patient-list/CoachFilter";
import useRole from "../../hooks/useRole";
import { theme } from "../../styles/theme";
import CustomModal from "../common/CustomModal";
import { patientAtom } from "recoil/patientAtom";
import { programDataAtom } from "recoil/programDataAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { onboardingDataState } from "../../recoil/onboardingDataAtom";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";
import { PATH } from "routes/paths";
import mixpanel from "mixpanel-browser";
import { notifyAppLogout } from "utils/webview";
import { BUILD_TARGET } from "config";
import { canAccessTaskEditor } from "pages/task-editor/utils/taskEditorAccess";

export default function SidebarPatientListForCoach() {
  const user = useRecoilValue(userAtom);
  const isCoach = useRole();

  const isInappWebview = useIsInappWebview();
  const backgroundColor = isInappWebview
    ? theme.vars.palette.Background.Level1
    : theme.vars.palette.Background.Level2;

  // CoachFilter 상태 관리
  const [selectedCoach, setSelectedCoach] = useState<string | undefined>(
    undefined
  );
  const [coachList, setCoachList] = useState<string[]>([]);

  // Logout modal state and handlers
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const resetUser = useResetRecoilState(userAtom);
  const resetPid = useResetRecoilState(patientAtom);
  const resetProgramData = useResetRecoilState(programDataAtom);
  const resetTaskState = useResetRecoilState(taskListAtom);
  const resetOnboardingData = useResetRecoilState(onboardingDataState);
  const resetHomeThoughtRecordState = useResetRecoilState(
    homeThoughtRecordListAtom
  );
  const canOpenTaskEditorFromSidebar = canAccessTaskEditor({
    buildTarget: BUILD_TARGET,
    hostname:
      typeof window === "undefined" ? undefined : window.location.hostname,
    user,
  });

  const handleLogout = async () => {
    setIsLoading(true);
    await logoutCoach();
    setIsLoading(false);
    setLogoutModalOpen(false);
    await initializeUser();
  };

  const initializeUser = async () => {
    resetUser();
    resetPid();
    resetProgramData();
    resetTaskState();
    resetOnboardingData();
    resetHomeThoughtRecordState();
    clearAuthHeaders();
    await notifyAppLogout();
    window.location.replace("coach");

    mixpanel.reset();
  };

  if (!isCoach) return <></>;

  const handleOpenTaskEditorInNewTab = () => {
    if (!canOpenTaskEditorFromSidebar) {
      return;
    }

    const taskEditorUrl = `${window.location.origin}${PATH.taskEditor}`;
    window.open(taskEditorUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet
      sx={{
        width: "220px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid",
        borderColor: "divider",
        backgroundColor: backgroundColor,
        overflow: "hidden",
      }}
    >
      {/* 헤더 영역 추가 */}
      <Box
        sx={{
          width: "100%",
          minHeight: "var(--StickyHeader-height)",
          height: "var(--StickyHeader-height)",
          position: "sticky",
          top: 0,
          backgroundColor: backgroundColor,
          zIndex: 10,
          px: "20px",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {user?.role === "admin" && coachList.length > 0 ? (
              <CoachFilter
                coachList={coachList}
                selectedCoach={selectedCoach}
                setSelectedCoach={setSelectedCoach}
              />
            ) : (
              <Typography
                variant={"Body"}
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                  px: 0,
                }}
              >
                담당 이용자
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* PatientList 영역 (스크롤 영역) */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: "8px",
          py: "8px",
          minHeight: 0,
          "&::-webkit-scrollbar": {
            width: "6px",
            opacity: 0,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "3px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0,0,0,0.4)",
          },
        }}
        className="scrollbar-hide"
      >
        <PatientList
          backgroundColor={backgroundColor}
          selectedCoach={selectedCoach}
          setCoachList={setCoachList}
        />
      </Box>

      {/* 하단 UserDropDown 영역 (고정 영역) */}
      <Box
        sx={{
          height: "60px",
          px: "10px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {user && isLoggedIn(user) && (
          <>
            <Stack
              direction={"row"}
              divider={
                <Typography
                  sx={{
                    px: 1,
                    color: theme.vars.palette.Content.Weak,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  variant={"Caption"}
                >
                  ·
                </Typography>
              }
              onClick={() => setLogoutModalOpen(true)}
            >
              <Typography
                variant={"Caption"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontWeight: 500,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                {user.nickname} 코치
              </Typography>
              <Typography
                variant={"Caption"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontWeight: 500,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                로그아웃
              </Typography>
            </Stack>
            <CustomModal
              open={logoutModalOpen}
              onClose={() => setLogoutModalOpen(false)}
              title={"정말로 로그아웃할까요?"}
              subtitle={"모든 기기와 브라우저에서 로그아웃돼요."}
              actions={[
                {
                  label: "로그아웃하기",
                  onClick: handleLogout,
                  variant: "Soft",
                  color: "danger",
                  buttonProps: {
                    loading: isLoading,
                    loadingIndicator: (
                      <CircularProgress variant="Soft" color={"danger"} />
                    ),
                  },
                },
              ]}
            />
          </>
        )}
      </Box>
    </Sheet>
  );
}
